import React, { useState } from 'react'
import { Box, Typography, IconButton, Chip } from '@mui/material'
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import PlayVideo from './Video'

export interface VideoRes {
  idVideo: number
  cloudflareVideoId: string
  sha256Hash: string
  thumbnailUrl: string
  thumbnailGifUrl: string
  playbackUrl: string
  streamUrl: string
  embedUrl: string
  status: string
  errorMessage: string
  duration: number
  width: number
  height: number
  sizeBytes: number
  titulo: string
  descripcion: string
  fechaCarga: string
  ready: boolean
  activo: boolean
}

interface Props {
  video: VideoRes
  setPreview: (video: any) => void
}

// Mapeo a los estados visuales. Cualquier status que no esté en el map
// (queued, inprogress, legacy pending/processing, o cualquier estado futuro) cae en PROCESANDO.
const STATUS_MAP: Record<string, { label: string; color: string }> = {
  ready: { label: 'DISPONIBLE', color: '#66bb6a' },
  error: { label: 'ERROR', color: '#e01a0b' },
  archived: { label: 'ARCHIVADO', color: '#2869A5' }
}
const STATUS_FALLBACK = { label: 'PROCESANDO', color: '#42a5f5' }

const ReadOnlyVideo: React.FC<Props> = ({ video, setPreview }: Props) => {
  const [openVideo, setOpenVideo] = useState<boolean>(false)
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setPreview(null)
  }

  const formatDate = (fechaISO: string) => {
    const fecha = new Date(fechaISO)

    const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
    const meses = [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre'
    ]

    const diaSemana = dias[fecha.getDay()]
    const dia = fecha.getDate()
    const mes = meses[fecha.getMonth()]

    let horas = fecha.getHours()
    const minutos = fecha.getMinutes().toString().padStart(2, '0')
    const periodo = horas >= 12 ? 'PM' : 'AM'

    horas = horas % 12 || 12

    return `${diaSemana} ${dia} de ${mes} - ${horas}:${minutos} ${periodo}`
  }

  const formatBytes = (bytes: number) => {
    const mb = bytes / (1000 * 1000)
    return mb.toFixed(1) + ' mb'
  }

  const display = STATUS_MAP[video.status] ?? STATUS_FALLBACK

  const isReady = video.status === 'ready'

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: 2,
          backgroundColor: '#2d2d2d',
          borderRadius: 2,
          padding: '12px 16px',
          cursor: isReady ? 'pointer' : 'auto',
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: '#3d3d3d'
          }
        }}
      >
        <Box
          onClick={() => isReady && setOpenVideo(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#4a4a4a',
            borderRadius: 1,
            width: 60,
            height: 60,
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
            cursor: isReady ? 'pointer' : 'auto'
          }}
        >
          <img
            src={video.thumbnailUrl}
            alt="Video thumbnail"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.3)'
            }}
          >
            <InsertPhotoOutlinedIcon sx={{ color: '#fff', fontSize: 36 }} />
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              color: '#fff',
              fontSize: '16px',
              fontWeight: 500,
              marginBottom: '2px'
            }}
          >
            {formatDate(video.fechaCarga)}
          </Typography>
          <Typography
            sx={{
              color: '#999',
              fontSize: '14px',
              marginBottom: '2px'
            }}
          >
            {video.duration} seg - {formatBytes(video.sizeBytes)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              sx={{
                color: '#999',
                fontSize: '14px'
              }}
            >
              Estado:
            </Typography>
            <Chip
              label={display.label}
              size="small"
              sx={{
                backgroundColor: display.color,
                color: '#fff',
                fontSize: '11px',
                height: '22px',
                fontWeight: 600,
                '& .MuiChip-label': {
                  padding: '0 10px'
                }
              }}
            />
          </Box>
          {video.errorMessage && (
            <Typography
              sx={{
                color: '#999',
                fontSize: '14px',
                marginBottom: '2px'
              }}
            >
              {video.errorMessage}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleDelete}
          sx={{
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <DeleteOutlineOutlinedIcon />
        </IconButton>
      </Box>
      <PlayVideo url={video.streamUrl} open={openVideo} setOpen={setOpenVideo} />
    </>
  )
}

export default ReadOnlyVideo
